/**
 * StripePackages.jsx – Phase 1b: Pakete & Preise
 * 
 * Zwei klar getrennte Bereiche:
 * - Legacy V1: Bestehende Stripe-Produkte (Read-Only)
 * - Billing V2: Neuer Paketkatalog (vollständiges CRUD)
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Package, Plus, Edit2, Copy, Eye, Archive, Send, CheckCircle2,
  AlertTriangle, Clock, Loader2, RefreshCw, ChevronDown, ChevronUp,
  X, Info, Shield, ExternalLink, Tag, Euro, Zap, Lock,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// ─── Status Badge ──────────────────────────────────────────────────────────
const STATUS_MAP = {
  draft:               { label: 'Entwurf',             color: 'bg-gray-100 text-gray-600 dark:bg-gray-800' },
  review:              { label: 'Zur Prüfung',          color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  stripe_sync_pending: { label: 'Stripe ausstehend',    color: 'bg-amber-100 text-amber-700' },
  stripe_sync_success: { label: 'Stripe sync ✓',        color: 'bg-green-100 text-green-700' },
  stripe_sync_failed:  { label: 'Stripe-Fehler',        color: 'bg-red-100 text-red-700' },
  published:           { label: 'Veröffentlicht',       color: 'bg-green-100 text-green-700 dark:bg-green-900/20' },
  not_bookable:        { label: 'Nicht buchbar',        color: 'bg-amber-100 text-amber-700' },
  archived:            { label: 'Archiviert',           color: 'bg-gray-100 text-gray-400' },
};

function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || STATUS_MAP.draft;
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.color}`}>{s.label}</span>;
}

function FmtEur({ cents }) {
  return <>{(cents / 100).toFixed(2)} €</>;
}

// ─── Paket-Karte ──────────────────────────────────────────────────────────
function PackageCard({ pkg, onEdit, onDuplicate, onArchive, onSync, onPreview, onPublish, onUnpublish, onAddPrice }) {
  const [expanded, setExpanded] = useState(false);
  const canPublish = pkg.publication_status === 'stripe_sync_success' && pkg.current_price_version_id;
  const isArchived = pkg.publication_status === 'archived';

  return (
    <div className={`bg-white dark:bg-gray-900 border rounded-xl overflow-hidden transition-all ${
      isArchived ? 'opacity-50 border-gray-200 dark:border-gray-700' :
      pkg.publication_status === 'published' ? 'border-green-300 dark:border-green-700' :
      'border-gray-200 dark:border-gray-700'
    }`} data-testid={`pkg-card-${pkg.id}`}>
      <div className="px-4 py-3.5 flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-bold truncate">{pkg.name}</p>
            <StatusBadge status={pkg.publication_status} />
            {pkg.badge && <span className="text-xs px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded-full font-medium">{pkg.badge}</span>}
            {pkg.is_highlighted && <Zap className="h-3.5 w-3.5 text-amber-500" title="Hervorgehoben" />}
          </div>
          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
            <span className="text-xs font-mono text-muted-foreground">{pkg.slug}</span>
            {pkg.current_price_version_id && (
              <span className="text-xs text-muted-foreground">
                {pkg.billing_interval === 'year' ? 'jährlich' : 'monatlich'}
              </span>
            )}
            {pkg.trial_enabled && (
              <span className="text-xs text-blue-600">{pkg.trial_days} Tage Trial</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {!isArchived && (
            <>
              <button onClick={() => onEdit(pkg)} title="Bearbeiten" className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400" data-testid={`edit-pkg-${pkg.id}`}><Edit2 className="h-3.5 w-3.5" /></button>
              <button onClick={() => onDuplicate(pkg)} title="Duplizieren" className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400" data-testid={`dup-pkg-${pkg.id}`}><Copy className="h-3.5 w-3.5" /></button>
              <button onClick={() => onPreview(pkg)} title="Vorschau" className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400" data-testid={`preview-pkg-${pkg.id}`}><Eye className="h-3.5 w-3.5" /></button>
              {!pkg.stripe_product_id && (
                <button onClick={() => onSync(pkg)} title="Stripe-Sync starten" className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-500" data-testid={`sync-pkg-${pkg.id}`}><Send className="h-3.5 w-3.5" /></button>
              )}
              {canPublish && (
                <button onClick={() => onPublish(pkg)} title="Veröffentlichen" className="p-1.5 rounded-lg hover:bg-green-50 text-green-600" data-testid={`publish-pkg-${pkg.id}`}><CheckCircle2 className="h-3.5 w-3.5" /></button>
              )}
              {pkg.publication_status === 'published' && (
                <button onClick={() => onUnpublish(pkg)} title="Nicht mehr buchbar" className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-500"><Lock className="h-3.5 w-3.5" /></button>
              )}
              <button onClick={() => onArchive(pkg)} title="Archivieren" className="p-1.5 rounded-lg hover:bg-red-50 text-red-400" data-testid={`archive-pkg-${pkg.id}`}><Archive className="h-3.5 w-3.5" /></button>
            </>
          )}
          <button onClick={() => setExpanded(v => !v)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400">
            {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 dark:border-gray-800 px-4 py-3 space-y-3 bg-gray-50 dark:bg-gray-800/20">
          {pkg.description_public && <p className="text-xs text-muted-foreground">{pkg.description_public}</p>}
          {pkg.features_public?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {pkg.features_public.map((f, i) => (
                <span key={i} className="text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-2 py-0.5 rounded-full">{f}</span>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {pkg.modules_included?.length > 0 && <span>Module: {pkg.modules_included.join(', ')}</span>}
            {pkg.setup_fee_net_cents > 0 && <span>Einrichtung: <FmtEur cents={pkg.setup_fee_net_cents} /></span>}
            {pkg.stripe_product_id && <span className="text-green-600 font-mono">{pkg.stripe_product_id.slice(0, 12)}…</span>}
          </div>
          {!isArchived && (
            <Button size="sm" variant="outline" onClick={() => onAddPrice(pkg)} className="text-xs h-7 gap-1" data-testid={`add-price-${pkg.id}`}>
              <Euro className="h-3 w-3" /> Preisversion hinzufügen
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Paket-Formular Modal ──────────────────────────────────────────────────
function PackageFormModal({ pkg, onClose, onSaved }) {
  const isEdit = !!pkg;
  const [form, setForm] = useState({
    name: pkg?.name || '',
    slug: pkg?.slug || '',
    description_public: pkg?.description_public || '',
    description_internal: pkg?.description_internal || '',
    features_public: (pkg?.features_public || []).join('\n'),
    modules_included: (pkg?.modules_included || []).join('\n'),
    billing_interval: pkg?.billing_interval || 'month',
    trial_enabled: pkg?.trial_enabled || false,
    trial_days: pkg?.trial_days || 30,
    trial_requires_payment_method: pkg?.trial_requires_payment_method !== false,
    trial_new_customers_only: pkg?.trial_new_customers_only !== false,
    setup_fee_net_cents: pkg?.setup_fee_net_cents || 0,
    sort_order: pkg?.sort_order || 0,
    badge: pkg?.badge || '',
    is_highlighted: pkg?.is_highlighted || false,
    cta_text: pkg?.cta_text || 'Jetzt starten',
  });
  const [saving, setSaving] = useState(false);

  const f = (key) => (e) => setForm(p => ({ ...p, [key]: e.target.value }));
  const fb = (key) => (e) => setForm(p => ({ ...p, [key]: e.target.checked }));

  const autoSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name ist Pflicht'); return; }
    if (!form.slug.trim()) { toast.error('Slug ist Pflicht'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        features_public: form.features_public.split('\n').map(s => s.trim()).filter(Boolean),
        modules_included: form.modules_included.split('\n').map(s => s.trim()).filter(Boolean),
        setup_fee_net_cents: parseInt(form.setup_fee_net_cents) || 0,
        sort_order: parseInt(form.sort_order) || 0,
        trial_days: parseInt(form.trial_days) || 0,
      };
      if (isEdit) {
        await axios.put(`${API}/api/superadmin/billing/packages/${pkg.id}`, payload, { headers: authHeader() });
        toast.success('Paket aktualisiert');
      } else {
        await axios.post(`${API}/api/superadmin/billing/packages`, payload, { headers: authHeader() });
        toast.success('Paket erstellt');
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white dark:bg-gray-900 w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[92vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
          <h2 className="text-base font-bold">{isEdit ? 'Paket bearbeiten' : 'Neues Paket erstellen'}</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"><X className="h-5 w-5" /></button>
        </div>
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          {/* Basisinfos */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Paketname *</Label>
              <Input value={form.name} onChange={e => { f('name')(e); if (!isEdit) setForm(p => ({ ...p, slug: autoSlug(e.target.value) })); }} placeholder="z.B. AllFlowX Pro" data-testid="pkg-name-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Slug * (URL-freundlich)</Label>
              <Input value={form.slug} onChange={f('slug')} placeholder="z.B. pro" className="font-mono" data-testid="pkg-slug-input" disabled={isEdit} />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Öffentliche Beschreibung</Label>
            <textarea value={form.description_public} onChange={f('description_public')} rows={2} className="w-full px-3 py-2 border rounded-xl text-sm bg-white dark:bg-gray-900 resize-none focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="Wird auf der Landingpage angezeigt" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Interne Beschreibung</Label>
            <textarea value={form.description_internal} onChange={f('description_internal')} rows={2} className="w-full px-3 py-2 border rounded-xl text-sm bg-white dark:bg-gray-900 resize-none focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="Nicht öffentlich sichtbar" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Öffentliche Features (eine pro Zeile)</Label>
              <textarea value={form.features_public} onChange={f('features_public')} rows={4} className="w-full px-3 py-2 border rounded-xl text-sm bg-white dark:bg-gray-900 resize-none focus:outline-none" placeholder="MHD-Kontrolle&#10;Unterweisungen&#10;Tresor" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Enthaltene Module (eine pro Zeile)</Label>
              <textarea value={form.modules_included} onChange={f('modules_included')} rows={4} className="w-full px-3 py-2 border rounded-xl text-sm bg-white dark:bg-gray-900 resize-none focus:outline-none" placeholder="tasks&#10;mhd&#10;backshop" />
            </div>
          </div>
          {/* Abrechnung */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Abrechnungsintervall</Label>
              <select value={form.billing_interval} onChange={f('billing_interval')} className="h-9 w-full rounded-lg border bg-background px-3 text-sm">
                <option value="month">Monatlich</option>
                <option value="year">Jährlich</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Einrichtungsgebühr (Cent, 0 = keine)</Label>
              <Input type="number" min="0" value={form.setup_fee_net_cents} onChange={f('setup_fee_net_cents')} placeholder="0" />
            </div>
          </div>
          {/* Trial */}
          <div className="space-y-2 p-3 bg-gray-50 dark:bg-gray-800/40 rounded-xl">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.trial_enabled} onChange={fb('trial_enabled')} className="h-4 w-4 rounded" data-testid="trial-enabled-checkbox" />
              <span className="text-sm font-medium">Testphase aktivieren</span>
            </label>
            {form.trial_enabled && (
              <div className="grid grid-cols-2 gap-3 ml-6">
                <div className="space-y-1">
                  <Label className="text-xs">Testphase (Tage)</Label>
                  <Input type="number" min="1" max="365" value={form.trial_days} onChange={f('trial_days')} data-testid="trial-days-input" />
                </div>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.trial_requires_payment_method} onChange={fb('trial_requires_payment_method')} className="h-4 w-4 rounded" />
                    <span className="text-xs">Zahlungsmethode erforderlich</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.trial_new_customers_only} onChange={fb('trial_new_customers_only')} className="h-4 w-4 rounded" />
                    <span className="text-xs">Nur Neukunden</span>
                  </label>
                </div>
              </div>
            )}
          </div>
          {/* Darstellung */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Badge (optional)</Label>
              <Input value={form.badge} onChange={f('badge')} placeholder="z.B. Beliebt" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Call-to-Action</Label>
              <Input value={form.cta_text} onChange={f('cta_text')} placeholder="Jetzt starten" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Sortierung</Label>
              <Input type="number" min="0" value={form.sort_order} onChange={f('sort_order')} />
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.is_highlighted} onChange={fb('is_highlighted')} className="h-4 w-4 rounded" data-testid="highlighted-checkbox" />
            <span className="text-sm">Paket hervorheben (auf Landingpage besonders markieren)</span>
          </label>
        </div>
        <div className="flex gap-3 px-5 py-4 border-t border-gray-100 dark:border-gray-800 flex-shrink-0">
          <Button variant="outline" onClick={onClose} className="flex-1" disabled={saving}>Abbrechen</Button>
          <Button onClick={handleSave} disabled={saving} className="flex-1" data-testid="save-pkg-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {isEdit ? 'Aktualisieren' : 'Als Entwurf erstellen'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Preis-Versionen Modal ─────────────────────────────────────────────────
function PriceVersionModal({ pkg, onClose, onSaved }) {
  const [form, setForm] = useState({ label: 'Standardpreis', price_net_cents_display: '', interval: pkg?.billing_interval || 'month' });
  const [saving, setSaving] = useState(false);
  const [versions, setVersions] = useState([]);

  useEffect(() => {
    if (pkg) {
      axios.get(`${API}/api/superadmin/billing/packages/${pkg.id}/price-versions`, { headers: authHeader() })
        .then(r => setVersions(r.data.price_versions || [])).catch(() => {});
    }
  }, [pkg]);

  const handleSave = async () => {
    const euros = parseFloat(form.price_net_cents_display.replace(',', '.'));
    if (isNaN(euros) || euros <= 0) { toast.error('Gültigen Preis eingeben (z.B. 40,00)'); return; }
    const price_net_cents = Math.round(euros * 100);
    setSaving(true);
    try {
      await axios.post(`${API}/api/superadmin/billing/packages/${pkg.id}/price-versions`, {
        label: form.label,
        price_net_cents,
        currency: 'eur',
        interval: form.interval,
      }, { headers: authHeader() });
      toast.success('Preisversion erstellt');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <div>
            <h2 className="text-base font-bold">Preisversion</h2>
            <p className="text-xs text-muted-foreground">{pkg?.name}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          {/* Wichtiger Hinweis */}
          <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 rounded-xl">
            <Info className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700 dark:text-amber-400">
              Bereits verwendete Stripe-Preise werden NICHT überschrieben. Jede Preisänderung erstellt eine neue Version.
            </p>
          </div>

          {/* Bestehende Versionen */}
          {versions.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Bestehende Versionen</p>
              {versions.map(v => (
                <div key={v.id} className="flex items-center justify-between p-2.5 bg-gray-50 dark:bg-gray-800/40 rounded-lg text-xs">
                  <span>v{v.version_number}: {v.label}</span>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{(v.price_net_cents / 100).toFixed(2)} €/{v.interval === 'year' ? 'Jahr' : 'Monat'}</span>
                    {v.stripe_price_id && <span className="text-green-600 font-mono">{v.stripe_price_id.slice(-8)}</span>}
                    {v.is_archived && <span className="text-gray-400">archiviert</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Neue Version */}
          <div className="space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Neue Preisversion</p>
            <div className="space-y-1">
              <Label className="text-xs">Bezeichnung</Label>
              <Input value={form.label} onChange={e => setForm(p => ({ ...p, label: e.target.value }))} placeholder="z.B. Einführungspreis" data-testid="price-label-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Nettopreis (€)</Label>
                <Input value={form.price_net_cents_display} onChange={e => setForm(p => ({ ...p, price_net_cents_display: e.target.value }))} placeholder="40,00" data-testid="price-amount-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Intervall</Label>
                <select value={form.interval} onChange={e => setForm(p => ({ ...p, interval: e.target.value }))} className="h-9 w-full rounded-lg border bg-background px-3 text-sm">
                  <option value="month">Monatlich</option>
                  <option value="year">Jährlich</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-3 px-5 py-4 border-t border-gray-100 dark:border-gray-800">
          <Button variant="outline" onClick={onClose} className="flex-1" disabled={saving}>Abbrechen</Button>
          <Button onClick={handleSave} disabled={saving} className="flex-1" data-testid="save-price-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Euro className="h-4 w-4 mr-2" />}
            Preisversion erstellen
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Stripe-Sync Bestätigungsdialog ────────────────────────────────────────
function StripeSyncModal({ pkg, stripeMode, onClose, onSynced }) {
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState(null);
  const isLive = stripeMode === 'live';

  const handleSync = async () => {
    if (isLive) return; // Doppelschutz
    setSyncing(true);
    try {
      const res = await axios.post(
        `${API}/api/superadmin/billing/packages/${pkg.id}/sync-stripe`,
        { confirmed: true },
        { headers: authHeader() }
      );
      setResult(res.data);
      toast.success('Stripe-Testprodukt und -preis erfolgreich erstellt!');
      // Pakete im Hintergrund neu laden, Modal bleibt offen für Ergebnis-Anzeige
      onSynced();
    } catch (e) {
      const err = e?.response?.data?.detail || 'Fehler beim Stripe-Sync';
      toast.error(err);
      if (err.includes('SICHERHEITSABBRUCH') || err.includes('Live')) {
        setResult({ error: err, is_live_abort: true });
      }
    } finally { setSyncing(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
          <h2 className="text-base font-bold flex items-center gap-2">
            <Send className="h-4 w-4 text-indigo-500" />
            Stripe-Synchronisation
          </h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          {isLive ? (
            <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/10 border border-red-300 rounded-xl">
              <Shield className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-red-700">SICHERHEITSABBRUCH – Live-Modus erkannt</p>
                <p className="text-xs text-red-600 mt-1">
                  Der Test-Sync ist ausschließlich im Stripe-Testmodus erlaubt.
                  Diese Umgebung verwendet einen Live-Stripe-Key. Es wurde keine Aktion ausgeführt.
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 rounded-xl">
                <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-amber-800 dark:text-amber-300">TESTMODUS – keine echten Zahlungen</p>
                  <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                    Es werden ausschließlich Stripe-Testobjekte erstellt ([TEST]-Prefix + Metadaten: environment=preview).
                    Es findet keine echte Zahlung statt. Keine Auswirkung auf bestehende Kunden.
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-semibold">Es wird erstellt:</p>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>• Stripe-Testprodukt: „[TEST] {pkg.name}"</li>
                  <li>• Stripe-Testpreis (aus aktiver Preisversion)</li>
                  <li>• Metadaten: environment=preview, billing_model=billing_v2</li>
                </ul>
              </div>
              {result && !result.error && (
                <div className="p-3 bg-green-50 dark:bg-green-900/10 border border-green-200 rounded-xl space-y-1">
                  <p className="text-sm font-semibold text-green-700">Sync erfolgreich!</p>
                  <p className="text-xs font-mono text-green-600">Product: {result.stripe_product_id}</p>
                  <p className="text-xs font-mono text-green-600">Price: {result.stripe_price_id}</p>
                </div>
              )}
            </>
          )}
        </div>
        <div className="flex gap-3 px-5 py-4 border-t border-gray-100 dark:border-gray-800">
          <Button variant="outline" onClick={onClose} className="flex-1">{result ? 'Schließen' : 'Abbrechen'}</Button>
          {!isLive && !result && (
            <Button onClick={handleSync} disabled={syncing} className="flex-1 bg-indigo-600 hover:bg-indigo-700" data-testid="confirm-stripe-sync-btn">
              {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Testprodukt erstellen
            </Button>
          )}
          {result && !result.error && (
            <div className="flex-1 flex items-center justify-center text-xs text-green-600 font-medium gap-1">
              <CheckCircle2 className="h-4 w-4" /> Sync abgeschlossen
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Hauptkomponente ────────────────────────────────────────────────────────
export default function StripePackages() {
  const [packages, setPackages] = useState([]);
  const [legacyProducts, setLegacyProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [legacyLoading, setLegacyLoading] = useState(true);
  const [showLegacy, setShowLegacy] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [stripeMode, setStripeMode] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editPkg, setEditPkg] = useState(null);
  const [syncPkg, setSyncPkg] = useState(null);
  const [pricePkg, setPricePkg] = useState(null);

  useEffect(() => {
    // Modus vom Backend holen
    axios.get(`${API}/api/superadmin/stripe-cc/sync/status`, { headers: authHeader() })
      .then(r => setStripeMode(r.data.mode || 'test')).catch(() => setStripeMode('test'));
  }, []);

  const loadPackages = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/superadmin/billing/packages?include_archived=${showArchived}`, { headers: authHeader() });
      setPackages(res.data.packages || []);
    } catch (e) {
      toast.error('Fehler beim Laden der Pakete');
    } finally { setLoading(false); }
  };

  const loadLegacy = async () => {
    setLegacyLoading(true);
    try {
      const res = await axios.get(`${API}/api/superadmin/billing/legacy-products`, { headers: authHeader() });
      setLegacyProducts(res.data.products || []);
    } catch (e) {
      toast.error('Fehler beim Laden der Legacy-Produkte');
    } finally { setLegacyLoading(false); }
  };

  useEffect(() => { loadPackages(); }, [showArchived]); // eslint-disable-line

  const handleDuplicate = async (pkg) => {
    try {
      await axios.post(`${API}/api/superadmin/billing/packages/${pkg.id}/duplicate`, {}, { headers: authHeader() });
      toast.success('Paket dupliziert');
      loadPackages();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Fehler'); }
  };

  const handleArchive = async (pkg) => {
    if (!window.confirm(`"${pkg.name}" archivieren? Keine Auswirkung auf bestehende Kunden.`)) return;
    try {
      await axios.post(`${API}/api/superadmin/billing/packages/${pkg.id}/archive`, {}, { headers: authHeader() });
      toast.success('Archiviert');
      loadPackages();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Fehler'); }
  };

  const handlePublish = async (pkg) => {
    if (!window.confirm(`"${pkg.name}" veröffentlichen? Das Paket erscheint dann auf der öffentlichen API.`)) return;
    try {
      await axios.post(`${API}/api/superadmin/billing/packages/${pkg.id}/publish`, {}, { headers: authHeader() });
      toast.success('Paket veröffentlicht!');
      loadPackages();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Fehler'); }
  };

  const handleUnpublish = async (pkg) => {
    if (!window.confirm(`"${pkg.name}" nicht mehr buchbar machen?`)) return;
    try {
      await axios.post(`${API}/api/superadmin/billing/packages/${pkg.id}/unpublish`, {}, { headers: authHeader() });
      toast.success('Paket nicht mehr buchbar');
      loadPackages();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Fehler'); }
  };

  const handlePreview = async (pkg) => {
    try {
      const res = await axios.post(`${API}/api/superadmin/billing/packages/${pkg.id}/preview-token`, {}, { headers: authHeader() });
      const url = `${process.env.REACT_APP_BACKEND_URL}${res.data.preview_url}`;
      window.open(url, '_blank');
      toast.info(`Vorschau-Link gültig für 24h`);
    } catch (e) { toast.error('Fehler beim Erstellen des Vorschau-Links'); }
  };

  return (
    <div className="space-y-6">
      {/* ── Legacy V1 ─────────────────────────────────────────────── */}
      <div className="space-y-3">
        <button
          onClick={() => { setShowLegacy(v => !v); if (!showLegacy && legacyProducts.length === 0) loadLegacy(); }}
          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800/60 transition-colors"
          data-testid="toggle-legacy-products"
        >
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-semibold">Legacy V1 – Bestehende Stripe-Produkte</span>
            <span className="text-xs px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full font-medium">Nur anzeigen</span>
          </div>
          {showLegacy ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
        </button>

        {showLegacy && (
          <div className="border border-amber-200 dark:border-amber-700 rounded-xl overflow-hidden">
            <div className="flex items-start gap-3 px-4 py-3 bg-amber-50 dark:bg-amber-900/10 border-b border-amber-200 dark:border-amber-700">
              <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700 dark:text-amber-400">
                <strong>Legacy V1 – Read-Only.</strong> Diese Produkte und Preise stammen direkt aus Stripe und dürfen nicht bearbeitet, archiviert oder migriert werden.
                Bestehende Kunden und Abonnements bleiben unverändert.
              </p>
            </div>
            {legacyLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            ) : legacyProducts.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">Keine Legacy-Produkte gefunden</p>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {legacyProducts.map(p => (
                  <div key={p.id} className="px-4 py-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold">{p.name}</p>
                      <span className="text-xs font-mono text-muted-foreground">{p.id}</span>
                    </div>
                    {p.prices.map(pr => (
                      <div key={pr.id} className="flex items-center gap-3 text-xs text-muted-foreground pl-2">
                        <span className="font-mono">{pr.id.slice(-10)}</span>
                        {pr.unit_amount != null && <span>{(pr.unit_amount / 100).toFixed(2)} €/{pr.interval}</span>}
                        <span className={pr.billing_scheme === 'tiered' ? 'text-blue-600' : ''}>{pr.billing_scheme}</span>
                        {!pr.active && <span className="text-red-500">inaktiv</span>}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Billing V2 ────────────────────────────────────────────── */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold">Billing V2 – Neuer Paketkatalog</h3>
            <p className="text-sm text-muted-foreground">Pakete erstellen und verwalten</p>
          </div>
          <div className="flex gap-2">
            <label className="flex items-center gap-2 cursor-pointer text-xs text-muted-foreground">
              <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} className="h-3.5 w-3.5 rounded" />
              Archivierte anzeigen
            </label>
            <Button size="sm" onClick={() => { setEditPkg(null); setShowForm(true); }} className="gap-1.5" data-testid="create-pkg-btn">
              <Plus className="h-4 w-4" /> Neues Paket
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : packages.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
            <Package className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="font-semibold">Noch keine Pakete</p>
            <p className="text-sm mt-1">Erstelle dein erstes Billing-V2-Paket</p>
            <Button className="mt-4 gap-1.5" onClick={() => setShowForm(true)} data-testid="create-first-pkg-btn">
              <Plus className="h-4 w-4" /> Jetzt erstellen
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {packages.map(pkg => (
              <PackageCard
                key={pkg.id}
                pkg={pkg}
                onEdit={(p) => { setEditPkg(p); setShowForm(true); }}
                onDuplicate={handleDuplicate}
                onArchive={handleArchive}
                onSync={(p) => setSyncPkg(p)}
                onPreview={handlePreview}
                onPublish={handlePublish}
                onUnpublish={handleUnpublish}
                onAddPrice={(p) => setPricePkg(p)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {showForm && (
        <PackageFormModal
          pkg={editPkg}
          onClose={() => { setShowForm(false); setEditPkg(null); }}
          onSaved={() => { setShowForm(false); setEditPkg(null); loadPackages(); }}
        />
      )}
      {syncPkg && (
        <StripeSyncModal
          pkg={syncPkg}
          stripeMode={stripeMode}
          onClose={() => { setSyncPkg(null); loadPackages(); }}
          onSynced={() => { loadPackages(); }}
        />
      )}
      {pricePkg && (
        <PriceVersionModal
          pkg={pricePkg}
          onClose={() => setPricePkg(null)}
          onSaved={() => { setPricePkg(null); loadPackages(); }}
        />
      )}
    </div>
  );
}
